import { computed, defineComponent, type CSSProperties, type FunctionalComponent } from 'vue'
import type { IEditableImageProps } from './types'
import { ref } from 'vue'
import TrashIcon from 'assets/img/svg/icons/trash-outline.svg?url'
import UploadIcon from 'assets/img/svg/icons/upload.svg?url'
import Button from '../button'
import Centered from '../centered'
import { Spinner } from '../spinner'
import { reactiveComputed, useFileDialog } from '@vueuse/core'
import { isFindexFile, type FindexFile } from '@/types/file'
import { isString } from '@/utils/is'

const EditableImage = defineComponent<IEditableImageProps>({
  name: 'EditableImage',
  setup(props) {
    const isLoading = ref<boolean>(false)

    const state = reactiveComputed<{
      fail: boolean
      uploadedFile: FindexFile | undefined
    }>(() => {
      const getInitialFile = () => {
        const fieldRef = props.fieldRef
        if (!fieldRef) return undefined
        const val = fieldRef.value.value
        if (!val) return undefined
        if (isFindexFile(val)) return val
        if (Array.isArray(val) && val.length > 0) {
          const first = val[0]
          if (isFindexFile(first)) return first
        }
        return undefined
      }

      return {
        fail: false,
        uploadedFile: getInitialFile()
      }
    })

    const handleFail = () => {
      state.fail = true
    }

    const fileDialog = useFileDialog({
      accept: 'image/*',
      directory: false,
      multiple: false
    })

    const toBase64 = (file: File) =>
      new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = () => {
          if (reader.result) {
            // @ts-ignore
            resolve(reader.result)
          } else {
            // @ts-ignore
            reject(reader.result)
          }
        }
        reader.onerror = reject
      })

    const handleUpload = async (files: FileList) => {
      isLoading.value = true

      try {
        const findexFiles: FindexFile[] = await Promise.all(
          Array.from(files).map(
            async (f): Promise<FindexFile> => ({ item: f, base64: await toBase64(f) })
          )
        )

        if (props.fieldRef) {
          //props.fieldRef.value.value = findexFiles

          if (props.reportChange) {
            props.reportChange(props.fieldRef, findexFiles)
          }
        }

        if (findexFiles && findexFiles.length > 0) {
          state.uploadedFile = findexFiles[0]
          const b64 = findexFiles[0].base64

          if (props.fieldRef && props.reportChange) {
            props.reportChange(props.fieldRef, b64)
          }
        }

        if (props.onUploaded) {
          await props.onUploaded(findexFiles)
        }
      } catch (e) {
        console.error(e)
      }
      isLoading.value = false
    }

    fileDialog.onChange((files: FileList | null) => {
      if (!files || files.length <= 0) return
      handleUpload(files).catch((e) => console.error(e))
    })

    const handleDelete = async () => {
      isLoading.value = true
      try {
        fileDialog.reset()

        if (props.onDelete) {
          await props.onDelete()
        }

        state.uploadedFile = undefined

        if (props.fieldRef && props.fieldRef.value) {
          if (props.reportChange) {
            props.reportChange(props.fieldRef, undefined, true)
          }
        }
      } catch (e) {
        console.error(e)
      }
      isLoading.value = false
    }

    const BottomBar: FunctionalComponent = () => {
      return (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: '50% 50%',
            width: '100%',
            gap: '0.5rem'
          }}
        >
          <Button
            disabled={!props.canUpload}
            onClick={() => fileDialog.open()}
            onlyIcon
            icon={{
              src: UploadIcon,
              stroke: 'white'
            }}
          />
          <Button
            disabled={!props.canDelete}
            onClick={handleDelete}
            onlyIcon
            variant="errorInfo"
            class="text-gray-500"
            icon={{
              src: TrashIcon,
              fill: 'currentColor'
            }}
          />
        </div>
      )
    }

    const imageStyle: CSSProperties = {
      width: '100%',
      height: '100%',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      aspectRatio: '1 / 1',
      objectFit: 'cover'
    }

    const source = computed(() => {
      if (state.uploadedFile && state.uploadedFile.base64) return state.uploadedFile.base64
      if (props.value) {
        if (isString(props.value)) return props.value
        if (isFindexFile(props.value) && props.value.base64) return props.value.base64
        if (Array.isArray(props.value) && props.value.length > 0) {
          const first = props.value[0]
          if (isFindexFile(first) && first.base64) return first.base64
        }
      }
      return props.src
    })

    const renderedImage = computed(() => {
      const ImageComponent = (
        props.imageComponent
          ? props.imageComponent
          : (props: any) => <img {...props} onError={handleFail} />
      ) as FunctionalComponent<any>

      return <ImageComponent style={imageStyle} onError={handleFail} src={source.value} />
    })

    return () => {
      return (
        <div
          style={{
            display: 'grid',
            gridTemplateRows: 'auto 2rem',
            gap: '0.5rem',
            width: '100%'
          }}
        >
          <div
            class="border border-gray-300 text-gray-500 text-center"
            style={{
              width: props.width || '5rem',
              aspectRatio: '1/1',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center'
            }}
          >
            {isLoading.value ? (
              <Centered>
                <Spinner />
              </Centered>
            ) : (state.fail || !source.value || source.value.length <= 1) &&
              props.placeholderComponent ? (
              <props.placeholderComponent />
            ) : (
              renderedImage.value
            )}
          </div>
          <BottomBar />
        </div>
      )
    }
  }
})

EditableImage.props = [
  'onDelete',
  'onUploaded',
  'imageComponent',
  'src',
  'canDelete',
  'canUpload',
  'fieldRef',
  'reportChange',
  'value',
  'width',
  'height',
  'placeholderComponent',
  'type'
]

export default EditableImage
